import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, FileText, Paperclip, Trash2 } from "lucide-react";
import { api, errCode, download } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useMaster, nameOf } from "../lib/master";
import { todayCL } from "../lib/time";
import { PageTitle, Sel, Inp } from "../components/Common";

const KINDS = ["lab", "residue", "market"];
const RESULTS = ["pending", "pass", "fail"];
const TONE = { pass: "bg-emerald-100 text-emerald-800", fail: "bg-red-100 text-red-800", pending: "bg-amber-100 text-amber-900" };
const EMPTY = { kind: "residue", csg_id: "", sector_id: "", variety_id: "", sample_date: todayCL(), lab_name: "", result: "pending", notes: "" };

export const ResultBadge = ({ result, t }) => <span className={`text-xs font-bold px-2 py-0.5 rounded ${TONE[result]}`}>{t(`lr_${result}`)}</span>;

export const ResidueAlerts = ({ alerts }) => {
  const { t } = useLang();
  const csgs = useMaster("csgs");
  const sectors = useMaster("sectors");
  if (!alerts.length) return null;
  return (
    <div className="card p-4 border-2 border-red-300 bg-red-50" data-testid="residue-alerts">
      <p className="font-bold flex items-center gap-2 text-red-800"><AlertTriangle size={18} />{t("residue_alert_title")} ({alerts.length})</p>
      <ul className="mt-2 text-sm space-y-1">{alerts.map((a) => (
        <li key={a.id} data-testid={`residue-alert-${a.id}`}><b>{nameOf(csgs, a.csg_id)}</b>{a.sector_id && ` · ${nameOf(sectors, a.sector_id)}`} · {a.sample_date}{a.lab_name && ` · ${a.lab_name}`}</li>))}</ul>
      <p className="text-xs text-red-700 mt-2">{t("residue_alert_hint")}</p>
    </div>
  );
};

const Files = ({ r, reload }) => {
  const { t, te } = useLang();
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/lab-results/${r.id}/files`, fd); toast.success(t("saved")); reload(); } catch (ex) { toast.error(te(errCode(ex))); }
    e.target.value = "";
  };
  return (
    <div className="space-y-1">
      {(r.files || []).map((f) => (
        <button key={f.id} className="text-xs underline flex items-center gap-1" onClick={() => download(`/lab-results/${r.id}/files/${f.id}`, f.filename)} data-testid={`lab-file-${f.id}`}>
          <FileText size={12} />{f.filename}</button>))}
      <label className="btn-secondary btn-sm cursor-pointer w-fit"><Paperclip size={14} />{t("attach")}
        <input type="file" className="hidden" onChange={up} data-testid={`lab-upload-${r.id}`} /></label>
    </div>
  );
};

export default function LabResults() {
  const { t, te } = useLang();
  const csgs = useMaster("csgs");
  const sectors = useMaster("sectors");
  const varieties = useMaster("varieties");
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState({ kind: "", result: "", csg_id: "" });
  const [f, setF] = useState(EMPTY);
  const onErr = (e) => toast.error(te(errCode(e)));
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    api.get("/lab-results", { params: Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) }).then((r) => { if (id === reqId.current) setRows(r.data); });
    api.get("/lab-results/alerts").then((r) => setAlerts(r.data));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () => api.post("/lab-results", { ...f, sector_id: f.sector_id || null, variety_id: f.variety_id || null, lab_name: f.lab_name || null, notes: f.notes || null })
    .then(() => { toast.success(t("saved")); setF({ ...EMPTY, kind: f.kind, csg_id: f.csg_id }); load(); }).catch(onErr);
  const setResult = (r, result) => api.patch(`/lab-results/${r.id}`, { result }).then(load).catch(onErr);
  const del = (r) => window.confirm(t("confirm_delete")) && api.delete(`/lab-results/${r.id}`).then(load).catch(onErr);
  const secOpts = sectors.filter((s) => s.csg_id === f.csg_id);
  const csgName = (id) => { const c = csgs.find((x) => x.id === id); return c ? `${c.code}${c.name ? ` · ${c.name}` : ""}` : "—"; };

  return (
    <div className="space-y-5" data-testid="lab-results-page">
      <PageTitle>{t("lab_title")}</PageTitle>
      <ResidueAlerts alerts={alerts} />
      <section className="card p-4 space-y-3" data-testid="lab-form">
        <h2 className="font-bold text-lg">{t("lab_new")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Sel label={t("lab_kind")} value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={KINDS.map((k) => ({ id: k, name: t(`lk_${k}`) }))} testid="lab-kind-select" />
          <Sel label="CSG *" value={f.csg_id} onChange={(v) => setF({ ...f, csg_id: v, sector_id: "" })} options={csgs.map((c) => ({ id: c.id, name: csgName(c.id) }))} placeholder={t("select")} testid="lab-csg-select" />
          <Sel label={`${t("sector")} (${t("optional")})`} value={f.sector_id} onChange={(v) => setF({ ...f, sector_id: v })} options={secOpts} placeholder={t("lab_whole_csg")} testid="lab-sector-select" />
          <Sel label={`${t("variety")} (${t("optional")})`} value={f.variety_id} onChange={(v) => setF({ ...f, variety_id: v })} options={varieties} placeholder={t("lab_all_varieties")} testid="lab-variety-select" />
          <Inp label={t("lab_sample_date")} type="date" value={f.sample_date} onChange={(e) => setF({ ...f, sample_date: e.target.value })} testid="lab-date-input" />
          <Inp label={t("lab_name")} value={f.lab_name} onChange={(e) => setF({ ...f, lab_name: e.target.value })} testid="lab-name-input" />
          <Sel label={t("lab_result")} value={f.result} onChange={(v) => setF({ ...f, result: v })} options={RESULTS.map((k) => ({ id: k, name: t(`lr_${k}`) }))} testid="lab-result-select" />
          <Inp label={t("notes")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} testid="lab-notes-input" />
        </div>
        <p className="text-xs text-slate-500">{t("lab_attach_hint")}</p>
        <button className="btn-primary" disabled={!f.csg_id || !f.sample_date} onClick={save} data-testid="lab-save-button">{t("save")}</button>
      </section>
      <div className="card p-3 flex flex-wrap gap-3" data-testid="lab-filters">
        <Sel label={t("lab_kind")} value={filter.kind} onChange={(v) => setFilter({ ...filter, kind: v })} options={KINDS.map((k) => ({ id: k, name: t(`lk_${k}`) }))} placeholder={t("all_results")} testid="lab-filter-kind" />
        <Sel label={t("lab_result")} value={filter.result} onChange={(v) => setFilter({ ...filter, result: v })} options={RESULTS.map((k) => ({ id: k, name: t(`lr_${k}`) }))} placeholder={t("all_results")} testid="lab-filter-result" />
        <Sel label="CSG" value={filter.csg_id} onChange={(v) => setFilter({ ...filter, csg_id: v })} options={csgs.map((c) => ({ id: c.id, name: csgName(c.id) }))} placeholder={t("all_results")} testid="lab-filter-csg" />
      </div>
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="lab-table">
          <thead><tr><th>{t("lab_sample_date")}</th><th>{t("lab_kind")}</th><th>CSG / {t("sector")}</th><th>{t("variety")}</th><th>{t("lab_name")}</th><th>{t("lab_result")}</th><th>{t("notes")}</th><th>{t("lab_files")}</th><th></th></tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={9} className="text-slate-500">{t("none")}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`lab-row-${r.id}`} className={r.result === "fail" ? "bg-red-50" : ""}>
                <td className="font-mono text-xs whitespace-nowrap">{r.sample_date}</td>
                <td className="font-semibold">{t(`lk_${r.kind}`)}</td>
                <td className="text-sm">{csgName(r.csg_id)}<br /><span className="text-xs text-slate-500">{r.sector_id ? nameOf(sectors, r.sector_id) : t("lab_whole_csg")}</span></td>
                <td className="text-sm">{r.variety_id ? nameOf(varieties, r.variety_id) : "—"}</td>
                <td className="text-sm">{r.lab_name || "—"}</td>
                <td><div className="flex flex-col gap-1"><ResultBadge result={r.result} t={t} />
                  <select className="text-xs border rounded px-1 py-0.5" value={r.result} onChange={(e) => setResult(r, e.target.value)} data-testid={`lab-change-result-${r.id}`}>
                    {RESULTS.map((k) => <option key={k} value={k}>{t(`lr_${k}`)}</option>)}</select></div></td>
                <td className="text-xs max-w-[220px]">{r.notes || "—"}<span className="block text-slate-400">{r.created_by}</span></td>
                <td><Files r={r} reload={load} /></td>
                <td><button onClick={() => del(r)} aria-label={t("delete")} data-testid={`lab-delete-${r.id}`}><Trash2 size={16} /></button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
